import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { workflowService } from "@/lib/api/services/workflow.service";
import { workflowKeys } from "../keys/workflows.keys";
import type { WorkflowFilters, RunFilters, CheckpointFilters } from "@/types/workflow.types";
import type {
  WorkflowReferenceOptionsParams,
  WorkflowReferenceResolveRef,
} from "@/types/workflow-reference.types";

interface QueryOptions {
  enabled?: boolean;
}

/** Fetch workflow list with optional filters */
export function useWorkflows(filters?: WorkflowFilters) {
  return useQuery({
    queryKey: workflowKeys.list(filters),
    queryFn: () => workflowService.list(filters),
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
  });
}

/** Fetch a single workflow by ID */
export function useWorkflow(id: string) {
  return useQuery({
    queryKey: workflowKeys.detail(id),
    queryFn: () => workflowService.getById(id),
    enabled: !!id,
  });
}

/** Fetch steps for a workflow */
export function useWorkflowSteps(workflowId: string) {
  return useQuery({
    queryKey: workflowKeys.steps(workflowId),
    queryFn: () => workflowService.listSteps(workflowId),
    enabled: !!workflowId,
  });
}

/** Fetch org-scoped MCP tool catalog for workflow node config. */
export function useWorkflowMCPTools() {
  return useQuery({
    queryKey: workflowKeys.mcpTools(),
    queryFn: () => workflowService.listMCPTools(),
    staleTime: 60 * 1000,
  });
}

/** Fetch backend workflow node catalog for schema-driven config forms. */
export function useWorkflowNodeTypes() {
  return useQuery({
    queryKey: workflowKeys.nodeTypes(),
    queryFn: () => workflowService.listNodeTypes(),
    staleTime: 5 * 60 * 1000,
  });
}

/** Fetch one workflow node type definition. Prefer list catalog when rendering many nodes. */
export function useWorkflowNodeType(type: string) {
  return useQuery({
    queryKey: workflowKeys.nodeType(type),
    queryFn: () => workflowService.getNodeType(type),
    enabled: !!type,
    staleTime: 5 * 60 * 1000,
  });
}

export function useWorkflowReferenceOptions(
  params: WorkflowReferenceOptionsParams,
  options: QueryOptions = {},
) {
  return useQuery({
    queryKey: workflowKeys.referenceOptions(params),
    queryFn: () => workflowService.listReferenceOptions(params),
    enabled: (options.enabled ?? true) && !!params.kind,
    placeholderData: keepPreviousData,
    staleTime: 10_000,
    refetchOnWindowFocus: true,
  });
}

export function useWorkflowReferenceResolve(
  refs: WorkflowReferenceResolveRef[],
  options: QueryOptions = {},
) {
  const hasIDs = refs.some((ref) => ref.ids.some(Boolean));
  return useQuery({
    queryKey: workflowKeys.referenceResolve(refs),
    queryFn: () => workflowService.resolveReferences({ refs }),
    enabled: (options.enabled ?? true) && hasIDs,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}

/** Fetch runs for a workflow */
export function useWorkflowRuns(workflowId: string, filters?: RunFilters) {
  return useQuery({
    queryKey: workflowKeys.runs(workflowId, filters),
    queryFn: () => workflowService.listRuns(workflowId, filters),
    enabled: !!workflowId,
    staleTime: 30 * 1000,
  });
}

/** Fetch a single workflow run */
export function useWorkflowRun(runId: string) {
  return useQuery({
    queryKey: workflowKeys.run(runId),
    queryFn: () => workflowService.getRun(runId),
    enabled: !!runId,
  });
}

/** Fetch step executions for a workflow run */
export function useWorkflowRunSteps(runId: string) {
  return useQuery({
    queryKey: workflowKeys.runSteps(runId),
    queryFn: () => workflowService.getRunSteps(runId),
    enabled: !!runId,
  });
}

/**
 * Checkpoint inbox for the current user.
 * 30s cadence balances badge freshness vs server load;
 * refetchIntervalInBackground=false pauses on tab hidden so an idle browser
 * does not burn polling.
 */
export function useMyCheckpoints(filters?: CheckpointFilters, options: QueryOptions = {}) {
  return useQuery({
    queryKey: workflowKeys.checkpointList(filters),
    queryFn: () => workflowService.listCheckpoints(filters),
    enabled: options.enabled ?? true,
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    staleTime: 25_000,
  });
}

/** Single checkpoint detail — approvers list + form data + isFormLocked flag. */
export function useCheckpoint(id: string | undefined) {
  return useQuery({
    queryKey: workflowKeys.checkpointDetail(id ?? ""),
    queryFn: () => workflowService.getCheckpoint(id!),
    enabled: !!id,
  });
}

/** Checkpoints for a specific workflow run — used to hydrate paused_checkpoint state on load. */
export function useRunCheckpoints(runId: string | undefined) {
  return useQuery({
    queryKey: workflowKeys.runCheckpoints(runId ?? ""),
    queryFn: () => workflowService.getRunCheckpoints(runId!),
    enabled: !!runId,
    staleTime: 10_000,
  });
}

/** Aggregate run metrics for a single workflow — sourced from GET /workflows/:id/stats. */
export function useWorkflowStats(workflowId: string) {
  return useQuery({
    queryKey: workflowKeys.stats(workflowId),
    queryFn: () => workflowService.getWorkflowStats(workflowId),
    enabled: !!workflowId,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

export function useWorkflowWebhooks(workflowId: string) {
  return useQuery({
    queryKey: workflowKeys.webhooks(workflowId),
    queryFn: () => workflowService.listWebhooks(workflowId),
    enabled: !!workflowId,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}

/** Paginated run event log — REST replay source and fallback polling target. */
export function useRunEvents(runId: string, filters?: { sinceId?: string; limit?: number }) {
  return useQuery({
    queryKey: workflowKeys.runEvents(runId, filters),
    queryFn: () => workflowService.getRunEvents(runId, filters),
    enabled: !!runId,
    staleTime: 5 * 1000,
    placeholderData: keepPreviousData,
  });
}
